import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  const mockChapters = useMemo(() => {
    if (!comic) return [];
    return Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
      id: comic.chapters - i,
      title: `第${comic.chapters - i}话 ${i === 0 ? '（最新）' : ''}`,
      date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      isNew: i < 3,
    }));
  }, [comic]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.category === comic.category)
      .slice(0, 6);
  }, [comic]);

  if (!comic) {
    return (
      <>
        <Breadcrumb />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
          <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </main>
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫星球`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2024-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫星球',
    },
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 韩漫星球`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。韩漫星球韩国漫画在线观看平台，下拉式韩漫阅读。`}
        keywords={`${comic.title},韩国漫画,${comic.tags.join(',')},韩漫网站,下拉式韩漫`}
        canonicalUrl={`https://manhwaplanet.com/comic/${comic.id}`}
        ogType="article"
        schema={articleSchema}
      />

      <Breadcrumb />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        {/* Comic Info */}
        <article className="flex flex-col md:flex-row gap-8">
          {/* Cover */}
          <div className="flex-shrink-0">
            <div className="w-48 md:w-56 rounded-xl overflow-hidden aspect-[3/4]">
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover"
                width={336}
                height={448}
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 flex flex-col justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{comic.title}</h1>
              <p className="text-sm text-foreground-500 mb-4">作者：{comic.author}</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                    {tag}
                  </span>
                ))}
                <span className={`px-3 py-1 text-xs rounded-full ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-background-200 text-foreground-600'
                }`}>
                  {comic.status}
                </span>
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                {comic.description}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-accent-600">{comic.rating}</div>
                  <div className="text-[11px] text-foreground-500">评分</div>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-foreground-900">{comic.views}</div>
                  <div className="text-[11px] text-foreground-500">人气</div>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-foreground-900">{comic.chapters}话</div>
                  <div className="text-[11px] text-foreground-500">章节数</div>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-foreground-900">{comic.updatedAt}</div>
                  <div className="text-[11px] text-foreground-500">最近更新</div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap">
                开始阅读第一话
              </button>
              <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-900 text-sm font-medium rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap">
                阅读最新话
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors duration-200 cursor-pointer">
                <i className="ri-heart-line"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-600 transition-colors duration-200 cursor-pointer">
                <i className="ri-share-forward-line"></i>
              </button>
            </div>
          </div>
        </article>

        {/* Chapter List */}
        <section className="mt-12">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">章节列表</h2>
          <div className="bg-background-100 rounded-xl overflow-hidden">
            <div className="divide-y divide-background-200/70">
              {mockChapters.map((chapter) => (
                <div
                  key={chapter.id}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-background-200/50 transition-colors duration-200 cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    {chapter.isNew && (
                      <span className="px-2 py-0.5 text-[10px] bg-accent-500 text-background-50 rounded-full whitespace-nowrap">NEW</span>
                    )}
                    <span className="text-sm text-foreground-800">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.date}</span>
                </div>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="px-5 py-4 text-center">
                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
                  查看全部 {comic.chapters} 话 <i className="ri-arrow-down-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} variant="default" />
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}